import React, { useEffect } from 'react';
import { Users, Calendar, MapPin, Target, Zap, Award, CheckCircle } from 'lucide-react';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const clients = [
    { name: "세메스", logo: `${process.env.PUBLIC_URL}/images/clients/semes.png` },
    { name: "아이마켓코리아", logo: `${process.env.PUBLIC_URL}/images/clients/imarket.png` },
    { name: "에이앤아이", logo: `${process.env.PUBLIC_URL}/images/clients/ani.png` },
    { name: "싸이맥스", logo: `${process.env.PUBLIC_URL}/images/clients/cymechs.jpg` },
    { name: "세스텍", logo: `${process.env.PUBLIC_URL}/images/clients/sestech.jpg` },
    { name: "메티스", logo: `${process.env.PUBLIC_URL}/images/clients/metis.png` },
    { name: "정수이앤씨", logo: `${process.env.PUBLIC_URL}/images/clients/jeongsoo.jpg` },
    { name: "한주하이텍", logo: `${process.env.PUBLIC_URL}/images/clients/hanjoo.jpg` },
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/banner.jpg)` }}
        >
          <div className="absolute inset-0 bg-slate-900/70"></div>
        </div>
        <div className="relative z-10 text-center px-4 animate-slide-up">
          <span className="text-accent font-bold tracking-widest uppercase mb-4 block">About Company</span>
          <h1 className="text-5xl md:text-6xl font-display font-bold text-white mb-6">회사 소개</h1>
          <p className="text-xl text-slate-200 max-w-2xl mx-auto font-light">
            혁신적인 기술과 신뢰를 바탕으로 미래를 선도하는 파워테크입니다.
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24">
        <div className="section-container">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-display font-bold text-slate-900 leading-tight">
                20년 이상의 노하우로<br />
                <span className="text-primary">최적의 솔루션</span>을 제공합니다.
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                파워테크는 2002년 설립 이래 반도체, 디스플레이, 자동화 장비 분야의
                하네스 케이블 및 전장 제어 시스템 전문 기업으로 성장해왔습니다.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                고객사의 요구사항을 최우선으로 생각하는 고객 중심 경영과
                끊임없는 기술 혁신을 통해 국내 주요 기업들의 신뢰할 수 있는 파트너로 자리매김하고 있습니다.
              </p>
              <div className="pt-4">
                <div className="inline-block bg-slate-50 border border-slate-200 rounded-xl p-6">
                  <p className="text-xl font-display font-bold text-slate-800 italic">
                    "2030년까지 국내 최고의 하네스 및 제어 시스템 전문 제조업체로 도약"
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 flex flex-col items-center text-center transform hover:-translate-y-2 transition-all duration-300">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center text-primary mb-4">
                  <UserIcon />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">대표자</h3>
                <p className="text-slate-600">양승우</p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 flex flex-col items-center text-center transform hover:-translate-y-2 transition-all duration-300 translate-y-8">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center text-primary mb-4">
                  <Calendar size={28} />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">설립일</h3>
                <p className="text-slate-600">2010년 7월 8일</p>
                <span className="text-xs text-slate-400 mt-1">(창업 2002년)</span>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 flex flex-col items-center text-center transform hover:-translate-y-2 transition-all duration-300">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center text-primary mb-4">
                  <Users size={28} />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">임직원</h3>
                <p className="text-slate-600">35명</p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 flex flex-col items-center text-center transform hover:-translate-y-2 transition-all duration-300 translate-y-8">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center text-primary mb-4">
                  <MapPin size={28} />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">소재지</h3>
                <p className="text-slate-600 text-sm">천안시 서북구 백석공단1로 70</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="section-container">
          <div className="text-center mb-16">
            <span className="text-accent font-bold tracking-widest uppercase">Our Core Values</span>
            <h2 className="text-4xl font-display font-bold mt-2">핵심 역량</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: <Target size={32} />, title: "정밀 제조 기술", desc: "전문화된 직원으로 인한 정밀 제조 기술" },
              { icon: <CheckCircle size={32} />, title: "품질 관리", desc: "품질보증팀의 엄격한 QA/QC" },
              { icon: <Zap size={32} />, title: "빠른 납기", desc: "최적화된 생산 시스템으로 빠른 납기" },
              { icon: <Award size={32} />, title: "맞춤형 솔루션", desc: "고객 니즈 완벽 대응" },
            ].map((value, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-colors duration-300">
                <div className="text-accent mb-6">{value.icon}</div>
                <h3 className="text-xl text-white font-bold mb-3">{value.title}</h3>
                <p className="text-slate-400 font-light">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Areas */}
      <section className="py-24 bg-slate-50">
        <div className="section-container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-display font-bold text-slate-900">주요 사업 분야</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="group relative overflow-hidden rounded-2xl shadow-lg">
              <div className="absolute inset-0 bg-slate-900/80 z-10 transition-opacity group-hover:opacity-90"></div>
              <img src={`${process.env.PUBLIC_URL}/images/c-box/cbox1.png`} alt="전장 C-BOX" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="relative z-20 p-10 h-full flex flex-col justify-end">
                <h3 className="text-3xl font-bold text-white mb-4">전장 C-BOX 제작</h3>
                <ul className="space-y-2 text-slate-200 mb-4 opacity-0 transform translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 delay-100">
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-accent rounded-full mr-2"></span>반도체/LCD 장비 제어기 제작 및 배선</li>
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-accent rounded-full mr-2"></span>CONTROLLER BOX ASS'Y</li>
                </ul>
                <div className="w-12 h-1 bg-accent transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-2xl shadow-lg">
              <div className="absolute inset-0 bg-slate-900/80 z-10 transition-opacity group-hover:opacity-90"></div>
              <img src={`${process.env.PUBLIC_URL}/images/harness/harness1.png`} alt="Cable Harness" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="relative z-20 p-10 h-full flex flex-col justify-end">
                <h3 className="text-3xl font-bold text-white mb-4">CABLE HARNESS 제작</h3>
                <ul className="space-y-2 text-slate-200 mb-4 opacity-0 transform translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 delay-100">
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-accent rounded-full mr-2"></span>CABLE WITH CONNECTOR</li>
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-accent rounded-full mr-2"></span>산업용 특수 케이블 가공</li>
                </ul>
                <div className="w-12 h-1 bg-accent transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="py-24 bg-white">
        <div className="section-container">
          <div className="text-center mb-16">
            <span className="text-secondary font-bold tracking-widest uppercase">Partners</span>
            <h2 className="text-4xl font-display font-bold text-slate-900 mt-2">주요 고객사</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 items-center">
            {clients.map((client, index) => (
              <div key={index} className="group p-4 flex flex-col items-center justify-center transition-all duration-300 hover:scale-105">
                <div className="h-20 w-full flex items-center justify-center mb-4 grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500">
                  <img
                    src={client.logo}
                    alt={client.name}
                    className="max-h-full max-w-full object-contain"
                    onError={(e) => (e.target as HTMLImageElement).src = `https://placehold.co/200x100/e2e8f0/64748b?text=${client.name}`}
                  />
                </div>
                <span className="text-sm font-medium text-slate-500 group-hover:text-slate-900 transition-colors">{client.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Helper for User Icon since 'User' is reserved/common
const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

export default About;
